import { useEffect, useState } from 'react';
import { Radio, Play, Pause, Volume2, VolumeX, Loader2, Headphones, SkipBack, SkipForward } from 'lucide-react';
import { useAudio } from '@/contexts/AudioContext';
import { formatDuration } from '@/lib/utils';

export function UniversalPlayer() {
  const {
    sourceType,
    isPlaying,
    volume,
    isMuted,
    nowPlaying,
    isLive,
    streamNowPlayingText,
    currentEpisode,
    currentTime,
    duration,
    isLoading,
    error,
    playStream,
    toggle,
    setVolume,
    toggleMute,
    seek,
    skipBy,
    playNextEpisode,
    playPreviousEpisode,
    hasPreviousEpisode,
    hasNextEpisode,
    clearError,
  } = useAudio();

  const [showVolume, setShowVolume] = useState(false);

  // Auto-hide error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(clearError, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    seek(Number(e.target.value));
  };

  // Determine what to show
  const isStream = sourceType === 'stream';
  const isPodcast = sourceType === 'podcast';
  const hasSource = isStream || isPodcast;

  return (
    <div className="bg-gradient-to-r from-primary/90 via-purple-600/90 to-accent/90 backdrop-blur-sm">
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center gap-3">
          {/* Left: Source indicator + Info */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Source Icon - clickable to switch to live stream */}
            <button
              type="button"
              onClick={playStream}
              className="shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors"
              title="Switch to Live Stream"
              aria-label="Switch to Live Stream"
            >
              {isStream && (
                <div className="relative">
                  <Radio className="w-5 h-5 text-white" />
                  {isLive && isPlaying && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  )}
                </div>
              )}
              {isPodcast && <Headphones className="w-5 h-5 text-white" />}
              {!hasSource && <Radio className="w-5 h-5 text-white/60" />}
            </button>

            {/* Info text */}
            <div className="min-w-0">
              {isStream && (
                <div className="flex items-center gap-2">
                  <span className="text-white/80 text-xs uppercase tracking-wider">Stream</span>
                  {isLive && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded font-medium">
                      LIVE
                    </span>
                  )}
                </div>
              )}
              {isPodcast && (
                <span className="text-white/80 text-xs uppercase tracking-wider">Podcast</span>
              )}
              {!hasSource && (
                <span className="text-white/60 text-xs uppercase tracking-wider">Ready</span>
              )}

              <div className="truncate">
                {isStream && (
                  <span className="text-white font-medium text-sm">
                    {streamNowPlayingText || nowPlaying?.title || 'Community programming'}
                  </span>
                )}
                {isPodcast && (
                  <span className="text-white font-medium text-sm">
                    {currentEpisode?.title ?? 'Select an episode'}
                  </span>
                )}
                {!hasSource && (
                  <span className="text-white/70 text-sm">Click play to listen</span>
                )}
              </div>

              {/* Podcast progress */}
              {isPodcast && duration > 0 && (
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-white/70 text-xs tabular-nums">
                    {formatDuration(currentTime)}
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={duration}
                    value={currentTime}
                    onChange={handleSeek}
                    className="flex-1 h-1 accent-white cursor-pointer min-w-[80px]"
                    aria-label="Seek"
                  />
                  <span className="text-white/70 text-xs tabular-nums">
                    {formatDuration(duration)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Controls */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Error message */}
            {error && (
              <span className="hidden sm:block text-xs text-white/90 max-w-[150px] truncate">
                {error}
              </span>
            )}

            {/* Loading indicator */}
            {isLoading && (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            )}

            {isPodcast && (
              <>
                <button
                  type="button"
                  onClick={() => skipBy(-15)}
                  className="hidden sm:flex items-center justify-center w-8 h-8 hover:bg-white/10 rounded-full text-white text-xs font-semibold"
                  aria-label="Back 15 seconds"
                >
                  -15
                </button>
                <button
                  type="button"
                  onClick={playPreviousEpisode}
                  disabled={!hasPreviousEpisode}
                  className="hidden sm:flex p-1.5 hover:bg-white/10 rounded-full disabled:opacity-40"
                  aria-label="Previous episode"
                >
                  <SkipBack className="w-4 h-4 text-white" />
                </button>
              </>
            )}

            {/* Play/Pause button */}
            <button
              type="button"
              onClick={toggle}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
              aria-label={isPlaying ? 'Pause' : hasSource ? 'Play' : 'Listen'}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-4 h-4 text-white fill-white" />
              ) : (
                <Play className="w-4 h-4 text-white fill-white" />
              )}
              <span className="text-white text-sm font-medium hidden sm:inline">
                {isPlaying ? 'Pause' : hasSource ? 'Play' : 'Listen'}
              </span>
            </button>

            {isPodcast && (
              <>
                <button
                  type="button"
                  onClick={playNextEpisode}
                  disabled={!hasNextEpisode}
                  className="hidden sm:flex p-1.5 hover:bg-white/10 rounded-full disabled:opacity-40"
                  aria-label="Next episode"
                >
                  <SkipForward className="w-4 h-4 text-white" />
                </button>
                <button
                  type="button"
                  onClick={() => skipBy(15)}
                  className="hidden sm:flex items-center justify-center w-8 h-8 hover:bg-white/10 rounded-full text-white text-xs font-semibold"
                  aria-label="Forward 15 seconds"
                >
                  +15
                </button>
              </>
            )}

            {/* Volume control */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowVolume(!showVolume)}
                onMouseEnter={() => setShowVolume(true)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Volume control"
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="w-4 h-4 text-white/70" />
                ) : (
                  <Volume2 className="w-4 h-4 text-white/70" />
                )}
              </button>

              {showVolume && (
                <div
                  className="absolute right-0 top-full mt-2 p-3 bg-secondary-600 rounded-lg shadow-lg border border-white/10"
                  onMouseLeave={() => setShowVolume(false)}
                >
                  <div className="flex items-center gap-2">
                    <button onClick={toggleMute} className="hover:text-white text-gray-400" aria-label={isMuted ? 'Unmute' : 'Mute'}>
                      {isMuted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={volume}
                      onChange={(e) => setVolume(Number(e.target.value))}
                      className="w-24 h-1 accent-primary cursor-pointer"
                      aria-label="Volume"
                    />
                  </div>
                </div>
              )}
            </div>


          </div>
        </div>
      </div>
    </div>
  );
}
